import { Inject, Injectable, OnDestroy } from "@angular/core";
import { MatLegacyDialogRef as MatDialogRef } from "@angular/material/legacy-dialog";
import { AccountAppCommand } from "@fabric-types/app-account";
import { select, Store } from "@ngrx/store";
import {
	OpenIdConnectCacheService,
	OpenIdConnectSelector,
	TWO_FACTOR_AUTHENTICATION_DIALOG_ID,
	twoFactorAuthVerificationDialogFactory,
} from "@obg/common.verification";
import { GtmLoginMethod } from "@obg/gtm.types/adaptive";
import {
	CACHE_STORE_KEY,
	CAFAuthenticatorService,
	COMPLETE_ACCOUNT_LOGIN_DIALOG_ID,
	CompleteAccountLoginDialogService,
	COVID_MESSAGE_DIALOG,
	COVID_MESSAGE_DIALOG_ID,
	COVID_MESSAGE_SHOWN,
	ENTITY_LEGAL_NOTICE_DIALOG_ID,
	EntityLegalNoticeDialogService,
	LATEST_PRIVACY_POLICY_DIALOG_ID,
	LatestPrivacyPolicyDialogService,
	LOGIN_CONSENTS_DIALOG_ID,
	LoginFlowDialogService,
	MARKETING_CONSENT_RENEWAL_DIALOG_ID,
	MIGRATED_USER_WELCOME_DIALOG_ID,
	NEW_SUSPICIOUS_LOGIN_DIALOG_ID,
	REQUEST_CPF_DIALOG_ID,
	SGA_LOGIN_DIALOG_ID,
	SgaVerificationService,
	SUSPICIOUS_LOGIN_DIALOG_ID,
	WelcomeMigratedUserConfigService,
	WelcomeMigratedUserDialogService,
} from "@obg/mat.ui.common";
import {
	LATEST_TC_DIALOG_ID,
	LatestTermsAndConditionsDialogService,
	UPDATED_TC_DIALOG_ID,
	UpdatedTermsAndConditionsAlertService,
	UpdatedTermsAndConditionsDialogService,
} from "@obg/mat.ui.common/terms-and-conditions";
import { DepositLimitRequiredDialogService, DialogComponentData, DialogService } from "@obg/mat.ui.core";
import {
	ConsentItem,
	ConsentsV2,
	MarketingSettingsAction,
	MarketingSettingsService,
	TermsAndConditionsConfigService,
	TwoFactorAuthAction,
	TwoFactorAuthFlow,
	TwoFactorAuthSelector,
	TwoFactorChallengeType,
	TwoFactorAuthConfigService,
} from "@obg/ngx.common";
import {
	AuthSelector,
	AuthTransactionConfigService,
	AuthTransactionSelector,
	CORE_ROUTE,
	CoreState,
	CovidMeasuresConfigService,
	Customer,
	CustomerManagementSelector,
	DepositLimitRequiredService,
	DgaAuthTransactionSelector,
	Jurisdiction,
	JurisdictionSelector,
	SessionAction,
	SessionSelector,
	STARTUP_CONTEXT_TOKEN,
	StartupContext,
	TwoFactorAuthMethod,
	VerificationStatus,
} from "@obg/ngx.core";
import {
	CacheFactory,
	CacheStore,
	ConfigService,
	CookieService,
	DeviceConfigService,
	DocumentRef,
	StorageMode,
} from "@obg/ngx.global";
import { RouteAction, RouteSelector } from "@obg/ngx.router";
import { ignoreNil } from "@obg/rxjs";
import { isBefore, subYears } from "date-fns";
import { combineLatest, EMPTY, merge, Observable, Subscription } from "rxjs";
import {
	distinctUntilChanged,
	filter,
	map,
	pairwise,
	startWith,
	switchMap,
	take,
	tap,
	withLatestFrom,
} from "rxjs/operators";

import {
	MARKETING_CONSENT_RENEWAL_DIALOG,
	MarketingConsentRenewalComponent,
	MarketingConsentRenewalData,
	MCR_COOKIE_KEY_SKIPPED,
	MCR_COOKIE_KEY_SKIPPED_BY,
} from "../../";
import { ObgConfigFeAdaptiveClientCommon } from "../../../common.config";
import { loginConsentsDialogFactory } from "../consents/login-consents.dialog";
import { RequestCpfDialogComponent } from "../face-auth/request-cpf-dialog/request-cpf-dialog.component";
import { FirstLoginService } from "../first-login/first-login.service";
import { LoginUiFlowConfigService } from "../login-ui/login-ui-flow.config";
import { LoginUiFlowService } from "../login-ui/login-ui-flow.service";
import { LoginQueryParams } from "../login.model";
import { sgaVerificationLoginDialogFactory } from "../sga/verification-login/verification-login.dialog";
import { VerificationLoginData } from "../sga/verification-login/verification-login.model";
import { SUSPICIOUS_LOGIN_DIALOG } from "../suspicious-login/suspicious-login.dialog";
import { WelcomeFlowService } from "../welcome-flow/welcome-flow.service";

@Injectable({ providedIn: "root" })
export class LoginFlowService implements OnDestroy {
	skipSgaVerification = false;
	private covidMessageCache: CacheStore;
	private data$$: Subscription | undefined;
	private dgaAuthMethod: GtmLoginMethod | undefined;
	private oidcMobileReturnUrl: string | undefined;
	private ksaJurisdiction = Jurisdiction.Ksa;
	private isMarketingConsentRenewalConfirmed = false;

	constructor(
		private documentRef: DocumentRef,
		private dialogService: DialogService,
		private loginUiFlowService: LoginUiFlowService,
		private store: Store<CoreState>,
		private termsDialogService: UpdatedTermsAndConditionsDialogService,
		private termsAlertService: UpdatedTermsAndConditionsAlertService,
		private termsAndConditionsDialogService: LatestTermsAndConditionsDialogService,
		private termsAndConditionsConfigService: TermsAndConditionsConfigService,
		private deviceConfigService: DeviceConfigService,
		private loginUiFlowConfigService: LoginUiFlowConfigService,
		private depositLimitRequiredService: DepositLimitRequiredService,
		private depositLimitRequiredDialogService: DepositLimitRequiredDialogService,
		private cacheFactory: CacheFactory,
		private covidMeasuresConfig: CovidMeasuresConfigService,
		private authTransactionSelector: AuthTransactionSelector,
		private loginFlowDialogService: LoginFlowDialogService,
		private entityLegalNoticeDialogService: EntityLegalNoticeDialogService,
		private sgaVerificationService: SgaVerificationService,
		private firstLoginService: FirstLoginService,
		private dgaAuthTransactionSelector: DgaAuthTransactionSelector,
		private oidcSelector: OpenIdConnectSelector,
		private openIdConnectCacheService: OpenIdConnectCacheService,
		private welcomeFlowService: WelcomeFlowService,
		private latestPrivacyPolicyDialogService: LatestPrivacyPolicyDialogService,
		private sessionSelector: SessionSelector,
		private marketingSettingsService: MarketingSettingsService,
		private cookieService: CookieService,
		private marketingSettingsAction: MarketingSettingsAction,
		private configService: ConfigService<ObgConfigFeAdaptiveClientCommon>,
		private migratedUserWelcomeScreen: WelcomeMigratedUserDialogService,
		private authTransactionConfig: AuthTransactionConfigService,
		private migratedUserConfig: WelcomeMigratedUserConfigService,
		private completeAccountDialog: CompleteAccountLoginDialogService,
		private cafAuthenticatorService: CAFAuthenticatorService,
		private sessionAction: SessionAction,
		private twoFactorAuthConfigService: TwoFactorAuthConfigService,
		@Inject(STARTUP_CONTEXT_TOKEN) private startupContext: StartupContext
	) {
		this.covidMessageCache = this.cacheFactory.get(CACHE_STORE_KEY, {
			storageMode: StorageMode.LocalStorage,
			maxAge: Number.MAX_VALUE,
		});
	}

	loginTransactionsVerificationStatus$(): Observable<VerificationStatus> {
		return this.store.pipe(
			select(this.authTransactionSelector.getLastStatus()),
			ignoreNil(),
			distinctUntilChanged()
		);
	}

	initialize(isMobile: boolean) {
		const authHandler$ = !isMobile ? this.authHandler$() : EMPTY;

		if (this.data$$) {
			return;
		}

		this.data$$ = merge(
			authHandler$,
			this.oidcMobileReturnUrl$(),
			this.dgaAuthMethod$(),
			this.gdprConsents$(),
			this.suspiciousLogin$(),
			this.newSuspiciousLogin$(),
			this.termsAndConditions$(isMobile),
			this.latestTermsAndConditions$(),
			this.latestPrivacyPolicy$(),
			this.entityLegalNotice$(),
			this.depositLimitRequired$(),
			this.sgaVerification$(isMobile),
			this.marketingConsentRenewal$(),
			this.detectFaceAuth$(),
			this.detectTwoFactorAuth$(),
			this.profileNotCompleted$()
		).subscribe();
	}

	dgaAuthMethod$(): Observable<GtmLoginMethod> {
		return combineLatest([
			this.store.select(this.dgaAuthTransactionSelector.getXmlDSig()),
			this.store.select(this.oidcSelector.getToken()),
		]).pipe(
			filter(
				([xmlSig, token]) =>
					this.startupContext.market.jurisdiction === Jurisdiction.Dga.toUpperCase() && !!(token || xmlSig)
			),
			take(1),
			map(() => (this.dgaAuthMethod = GtmLoginMethod.MitID))
		);
	}

	oidcMobileReturnUrl$(): Observable<string> {
		return this.openIdConnectCacheService.getReturnUrl$().pipe(
			take(1),
			map((url: string | undefined) => (this.oidcMobileReturnUrl = url ? url : "/"))
		);
	}

	sgaVerification$(hideFooter?: boolean, verificationLoginData?: VerificationLoginData) {
		return this.sgaVerificationService.sgaVerification$().pipe(
			withLatestFrom(this.store.select(JurisdictionSelector.get).pipe(ignoreNil())),
			filter(
				([, jurisdiction]) =>
					!this.skipSgaVerification &&
					jurisdiction === Jurisdiction.Sga &&
					!this.authTransactionConfig.getBankIdSecureStartEnabled()
			),
			tap(() => {
				const open = () => {
					const data: DialogComponentData = {
						title: "sga-verification-transaction.label",
						hideFooter,
					};

					this.dialogService.openComponent(sgaVerificationLoginDialogFactory(verificationLoginData), {
						data,
						disableClose: true,
					});
				};

				this.loginFlowDialogService.openDialog(open, SGA_LOGIN_DIALOG_ID);
			})
		);
	}

	gdprConsents$() {
		return this.loginUiFlowService.shouldShowConsents$().pipe(
			withLatestFrom(this.store.pipe(RouteSelector.getName$)),
			tap(([, currentRoute]) => {
				const open = () => {
					// TODO: clean this up (OBGB-10023)
					const headerAndFooter =
						!this.deviceConfigService.isDesktop() &&
						this.loginUiFlowConfigService.showHeaderAndFooterForNDCflow();
					const data: DialogComponentData = {
						navbar: headerAndFooter,
						navbarOptions: {
							hideBackButton: true,
							showCloseButton: currentRoute === CORE_ROUTE.auth.login,
							preventDefaultBack: true,
						},
						hideClose: true,
						hideFooter: !headerAndFooter,
						title: headerAndFooter ? "login-consents.caption.generic" : undefined,
					};
					this.dialogService.openComponent(
						loginConsentsDialogFactory(this.dgaAuthMethod ?? GtmLoginMethod.Credentials),
						{ disableClose: true, data, height: "initial" }
					);
				};

				this.welcomeFlowService.triggerABTestEvent();
				this.loginFlowDialogService.openDialog(open, LOGIN_CONSENTS_DIALOG_ID);
			})
		);
	}

	termsAndConditions$(showTermsAndConditionsInAlert?: boolean) {
		return this.loginUiFlowService.detectTermsAndConditions$().pipe(
			withLatestFrom(
				this.store.select(CustomerManagementSelector.getTermsAndConditionsJurisdiction),
				this.termsAndConditionsConfigService.showFullContentInDialog$()
			),
			tap(([, jurisdiction, showFullContentInDialog]) => {
				if (showFullContentInDialog) {
					this.loginFlowDialogService.openDialog(
						this.termsAndConditionsDialogService.open(this.deviceConfigService.isDesktop()),
						LATEST_TC_DIALOG_ID
					);
					return;
				}
				if (showTermsAndConditionsInAlert && jurisdiction) {
					this.loginFlowDialogService.closeAllDialogs();
					this.termsAlertService.open(jurisdiction);
					return;
				}
				this.loginFlowDialogService.openDialog(this.termsDialogService.open(), UPDATED_TC_DIALOG_ID);
			})
		);
	}

	detectFaceAuth$() {
		return this.loginUiFlowService.detectTwoFactorAuth$().pipe(
			filter(
				(twoFactorDetails) =>
					twoFactorDetails.challengeDetails?.communicationMethods[0]?.channel === "Biometric" ||
					twoFactorDetails.challengeDetails?.primaryCommunicationMethod.channel === "Biometric"
			),
			switchMap(() => {
				const cpfDialog = this.dialogService.openComponent(RequestCpfDialogComponent, {
					id: REQUEST_CPF_DIALOG_ID,
					disableClose: true,
					height: this.deviceConfigService.isDesktop() ? "450px" : "100vh",
				});

				return cpfDialog.afterClosed().pipe(
					tap((dialogResult) => {
						this.documentRef.obgEventBus.listen(
							AccountAppCommand.FaceAuthenticationSuccessful,
							(payload) => {
								if (payload.isValid && payload.authenticationToken) {
									this.store.dispatch(
										this.sessionAction.loginWithToken({
											token: payload.authenticationToken,
										})
									);
								}
							}
						);

						return this.cafAuthenticatorService.openCafAuthenticator$(dialogResult);
					})
				);
			})
		);
	}

	detectTwoFactorAuth$() {
		return this.loginUiFlowService.detectTwoFactorAuth$().pipe(
			withLatestFrom(this.store.pipe(select(JurisdictionSelector.get))),
			filter(
				([twoFactorDetails]) =>
					twoFactorDetails.challengeDetails?.communicationMethods[0]?.channel !== "Biometric" &&
					twoFactorDetails.challengeDetails?.primaryCommunicationMethod?.channel !== "Biometric"
			),
			tap(([twoFactorDetails, jurisdiction]) => {
				const open = () => {
					this.dialogService.openComponent(
						twoFactorAuthVerificationDialogFactory({
							authFlow: TwoFactorAuthFlow.LoginFlow,
							hideRememberDevice: jurisdiction === Jurisdiction.BRA,
							isAccountNotVerified: twoFactorDetails.isAccountNotVerified,
							forceMethod: this.twoFactorAuthConfigService.getIsEmailPrimaryMethodEnabled()
								? TwoFactorAuthMethod.EmailTwoFactorAuthenticationMethod
								: undefined,
						}),
						{
							id: TWO_FACTOR_AUTHENTICATION_DIALOG_ID,
							disableClose: true,
						}
					);
				};
				this.loginFlowDialogService.openDialog(open, TWO_FACTOR_AUTHENTICATION_DIALOG_ID);
			})
		);
	}

	suspiciousLogin$() {
		return this.loginUiFlowService.suspiciousLogin$().pipe(
			tap(() => {
				const open = () => {
					this.dialogService.openComponent(SUSPICIOUS_LOGIN_DIALOG, {
						id: SUSPICIOUS_LOGIN_DIALOG_ID,
						disableClose: false,
						data: {
							hideFooter: true,
						},
					});
				};
				this.loginFlowDialogService.openDialog(open, SUSPICIOUS_LOGIN_DIALOG_ID);
			})
		);
	}

	newSuspiciousLogin$() {
		return this.loginUiFlowService.newSuspiciousLogin$().pipe(
			tap(() => {
				const open = () => {
					this.dialogService.openComponent(
						twoFactorAuthVerificationDialogFactory({
							authFlow: TwoFactorAuthFlow.LoginFlow,
							forceMethod: TwoFactorAuthMethod.EmailTwoFactorAuthenticationMethod,
							dialogId: NEW_SUSPICIOUS_LOGIN_DIALOG_ID,
							hideRememberDevice: true,
							suspicious: true,
						}),
						{
							id: NEW_SUSPICIOUS_LOGIN_DIALOG_ID,
							disableClose: true,
						}
					);
				};
				this.loginFlowDialogService.openDialog(open, NEW_SUSPICIOUS_LOGIN_DIALOG_ID);
			})
		);
	}

	profileNotCompleted$() {
		return this.loginUiFlowService.detectProfileNotCompleted$().pipe(
			tap(() => {
				if (this.migratedUserConfig.getShowWelcomePage()) {
					this.loginFlowDialogService.openDialog(
						this.migratedUserWelcomeScreen.open(),
						MIGRATED_USER_WELCOME_DIALOG_ID
					);
					return;
				}

				this.loginFlowDialogService.openDialog(
					this.completeAccountDialog.open,
					COMPLETE_ACCOUNT_LOGIN_DIALOG_ID
				);
			})
		);
	}

	latestTermsAndConditions$() {
		return this.loginUiFlowService
			.detectLatestTermsAndConditions$()
			.pipe(
				tap(() =>
					this.loginFlowDialogService.openDialog(
						this.termsAndConditionsDialogService.open(this.deviceConfigService.isDesktop()),
						LATEST_TC_DIALOG_ID
					)
				)
			);
	}

	latestPrivacyPolicy$() {
		return this.loginUiFlowService
			.detectLatestPrivacyPolicy$()
			.pipe(
				tap(() =>
					this.loginFlowDialogService.openDialog(
						this.latestPrivacyPolicyDialogService.open(),
						LATEST_PRIVACY_POLICY_DIALOG_ID
					)
				)
			);
	}

	entityLegalNotice$() {
		return this.loginUiFlowService
			.detectEntityLegalNotice$()
			.pipe(
				tap(() =>
					this.loginFlowDialogService.openDialog(
						this.entityLegalNoticeDialogService.open(),
						ENTITY_LEGAL_NOTICE_DIALOG_ID
					)
				)
			);
	}

	authHandler$(navigateBackAfterSuccessfulLogin?: boolean) {
		return this.store.select(AuthSelector.isAuthenticated).pipe(
			filter((isAuth) => isAuth),
			withLatestFrom(
				this.store.pipe(RouteSelector.getName$),
				this.store.select<LoginQueryParams | undefined>(RouteSelector.getQueryParams),
				this.store.select(TwoFactorAuthSelector.getSelectedChallengeType)
			),
			switchMap(([, routeName, params, challengeType]) =>
				this.firstLoginService.shouldShowWelcomeFlow$().pipe(
					map((shouldShowWelcomeFlow) => ({
						shouldShowWelcomeFlow,
						routeName,
						params,
						challengeType,
					}))
				)
			),
			tap(({ shouldShowWelcomeFlow, routeName, params, challengeType }) => {
				this.loginFlowDialogService.closeAllDialogs();
				this.handlePostLoginNavigation(
					routeName,
					params,
					navigateBackAfterSuccessfulLogin,
					shouldShowWelcomeFlow
				);
				if (challengeType === TwoFactorChallengeType.SuspiciousLogin) {
					this.store.dispatch(TwoFactorAuthAction.resetOnSuspiciousLogin());
				}
			}),
			switchMap(() => this.handleCovidMessage$())
		);
	}

	/**
	 * Open Marketing Consent Renewal dialog if any of the criteria are met.
	 */
	marketingConsentRenewal$() {
		const isFeatureEnabled = this.configService.get((x) => x.common.isMarketingRenewalDialogEnabled);

		if (!isFeatureEnabled) {
			return EMPTY;
		}

		const isMobile = !this.deviceConfigService.isDesktop();
		const customer$ = this.store.select(CustomerManagementSelector.getCustomer).pipe(filter<Customer>(Boolean));
		const prevRouteName$ = this.store.pipe(RouteSelector.getName$).pipe(
			filter(() => isMobile),
			pairwise(),
			map(([prev]) => prev),
			startWith("")
		);
		const sgaAlertClosed$ = this.firstLoginService.sgaLoginAlertClosed$.pipe(startWith(false));

		return combineLatest([customer$, prevRouteName$, sgaAlertClosed$]).pipe(
			withLatestFrom(
				this.store.select(this.sessionSelector.isAuthenticated()),
				this.store.select(JurisdictionSelector.get)
			),
			filter(([[, , sgaAlertClosed], isAuthenticated, jurisdiction]) =>
				jurisdiction === Jurisdiction.Sga ? isAuthenticated && sgaAlertClosed : isAuthenticated
			),
			switchMap(([[customer, prevRouteName]]) =>
				this.marketingSettingsService
					.getConsentsV2()
					.pipe(map(({ consents }) => ({ consents, customer, prevRouteName })))
			),
			tap(({ consents, customer, prevRouteName }) => {
				if (!consents || this.isMarketingConsentRenewalConfirmed) {
					return;
				}

				const skippedCookieValue = !!this.cookieService.get(MCR_COOKIE_KEY_SKIPPED);
				const skippedByCookieValue = Number(this.cookieService.get(MCR_COOKIE_KEY_SKIPPED_BY)) || 0;

				// This will exclude cookie and third-party consents as they are not relevant to marketing consents.
				const relevantConsents: (keyof ConsentsV2)[] = ["groupMarketing", "brandMarketing"];

				const consentLimitDate = subYears(new Date(), 5);

				const isInvalidConsent = (consent: ConsentItem) =>
					consent?.createdDate ? isBefore(new Date(consent.createdDate), consentLimitDate) : false;

				const hasInvalidConsent = relevantConsents
					.map((consentKey) => consents[consentKey])
					.filter((consent) => consent)
					.some(isInvalidConsent);

				/*
				 * On mobile we want to show the dialog only if the user navigated from the login page.
				 * On desktop login opened in a dialog so we don't need to check the previous route.
				 */
				const navigateFromLoginOnMobile = !this.deviceConfigService.isDesktop()
					? prevRouteName === CORE_ROUTE.auth.login
					: true;

				let shouldOpenDialog =
					hasInvalidConsent && !skippedCookieValue && skippedByCookieValue < 3 && navigateFromLoginOnMobile;

				if (isMobile) {
					shouldOpenDialog = shouldOpenDialog && prevRouteName === CORE_ROUTE.auth.login;
				}

				if (!shouldOpenDialog) {
					return;
				}

				const data: MarketingConsentRenewalData = {
					consents: {
						brandMarketing: consents.brandMarketing.hasApproved,
						groupMarketing: consents.groupMarketing.hasApproved,
					},
					subscriptions: customer.subscriptions,
				};

				const openHandler = () => {
					const dialog: MatDialogRef<
						MarketingConsentRenewalComponent,
						MarketingConsentRenewalData | undefined
					> = this.dialogService.openComponent(MARKETING_CONSENT_RENEWAL_DIALOG, {
						id: MARKETING_CONSENT_RENEWAL_DIALOG_ID,
						data,
						disableClose: true,
					});

					// eslint-disable-next-line rxjs/no-ignored-subscription
					dialog.afterClosed().subscribe((dialogResult) => {
						if (dialogResult) {
							this.store.dispatch(
								this.marketingSettingsAction.updateMarketingSettingsFromLoginFlow(dialogResult)
							);
							this.isMarketingConsentRenewalConfirmed = true;
							return;
						}

						const skippedExpiryDate = new Date();
						skippedExpiryDate.setDate(skippedExpiryDate.getDate() + 7);

						const skippedByExpiryDate = new Date();
						skippedByExpiryDate.setDate(skippedExpiryDate.getFullYear() + 1);

						const skippedBy = Number(this.cookieService.get(MCR_COOKIE_KEY_SKIPPED_BY));

						this.cookieService.set({
							name: MCR_COOKIE_KEY_SKIPPED,
							value: 1,
							expiryDate: skippedExpiryDate,
						});

						this.cookieService.set({
							name: MCR_COOKIE_KEY_SKIPPED_BY,
							value: !isNaN(skippedBy) ? skippedBy + 1 : 1,
							expiryDate: skippedByExpiryDate,
						});
					});
				};

				this.loginFlowDialogService.openDialog(openHandler, MARKETING_CONSENT_RENEWAL_DIALOG_ID);
			})
		);
	}

	depositLimitRequired$() {
		return this.depositLimitRequiredService
			.showDepositLimitsDialog$()
			.pipe(tap(() => this.depositLimitRequiredDialogService.openDialog()));
	}

	unsubscribe() {
		this.data$$?.unsubscribe();
	}

	ngOnDestroy() {
		this.unsubscribe();
	}

	private handlePostLoginNavigation(
		currentRoute: string,
		params?: LoginQueryParams,
		navigateBackAfterSuccessfulLogin?: boolean,
		firstLogin?: boolean
	) {
		const isLoginPage = currentRoute === CORE_ROUTE.auth.login;
		const isOidcMobileLoginPage =
			currentRoute === CORE_ROUTE.auth.openIdConnect && !this.deviceConfigService.isDesktop();
		const returnUrl = isOidcMobileLoginPage ? this.oidcMobileReturnUrl : params?.returnUrl;

		if (firstLogin) {
			this.store.select(JurisdictionSelector.get).pipe(
				ignoreNil(),
				tap((jurisdiction) => {
					if (jurisdiction === this.ksaJurisdiction) {
						this.store.dispatch(RouteAction.navigate(CORE_ROUTE.verification.root));
					} else {
						this.store.dispatch(RouteAction.navigate(currentRoute));
					}
				})
			);
			return;
		}

		if (navigateBackAfterSuccessfulLogin && !returnUrl && currentRoute !== CORE_ROUTE.auth.openIdConnect) {
			this.store.dispatch(RouteAction.back());
			return;
		}

		if (!isLoginPage && !returnUrl) {
			return;
		}

		this.store.dispatch(
			returnUrl
				? RouteAction.navigateByUrl(returnUrl, { replaceUrl: true })
				: RouteAction.navigate(CORE_ROUTE.home)
		);
	}

	private handleCovidMessage$() {
		return this.store
			.select(CustomerManagementSelector.getCustomer)
			.pipe(
				ignoreNil(),
				take(1),
				withLatestFrom(
					this.store.select(AuthSelector.isFirstLogin),
					this.store.pipe(RouteSelector.getName$),
					this.covidMessageCache.getItemAsObservable<boolean>(COVID_MESSAGE_SHOWN)
				),
				map(([customer, isFirstLogin, routeName, messageShown]) => {
					const disabledRoutes = [
						CORE_ROUTE.auth.login,
						CORE_ROUTE.pokerCasinoGallery,
						CORE_ROUTE.pokerDepositWithdraw,
					];

					return (
						this.covidMeasuresConfig.covidMeasuresEnabled() &&
						!isFirstLogin &&
						!disabledRoutes.includes(routeName) &&
						customer.jurisdiction === Jurisdiction.Ukgc &&
						!messageShown
					);
				})
			)
			.pipe(tap((showCovidMessage) => showCovidMessage && this.handlePostLoginMessage()));
	}

	private handlePostLoginMessage() {
		const data: DialogComponentData = {
			hideClose: false,
			hideFooter: false,
			title: "responsible-gaming.title",
		};

		const open = () => {
			this.dialogService.openComponent(COVID_MESSAGE_DIALOG, {
				id: COVID_MESSAGE_DIALOG_ID,
				disableClose: false,
				data,
			});
		};

		this.loginFlowDialogService.openDialog(open, COVID_MESSAGE_DIALOG_ID);
	}
}
